import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { Loader2, Brain, ArrowRight, ChevronLeft, Sparkles, ShieldCheck, Heart } from "lucide-react";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { motion, AnimatePresence } from 'framer-motion';

interface IndependentLoginProps {
  isSignup?: boolean;
}

const BASE_FUNCTION_URL = 'https://us-central1-cbsms-da9ce.cloudfunctions.net/createUser';

const IndependentLogin = ({ isSignup: initialIsSignup = false }: IndependentLoginProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(initialIsSignup);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const auth = getAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validateInputs = (): boolean => {
    if (!email || !password) {
      setError('Email and password are required.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email format.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }
    if (isSignup && (!name || name.trim().length < 2)) {
      setError('Please enter your full name.');
      return false;
    }
    if (isSignup && password !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    return true;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!validateInputs()) {
      setLoading(false);
      return;
    }

    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not ready. Please try again.');
      }
      const recaptchaToken = await executeRecaptcha(isSignup ? 'independent_signup' : 'independent_login');

      const verifyResp = await fetch(`${BASE_FUNCTION_URL}/verify-recaptcha`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          recaptchaToken, 
          expectedAction: isSignup ? 'independent_signup' : 'independent_login' 
        }),
      });
      
      if (!verifyResp.ok) {
        throw new Error('reCAPTCHA verification failed.');
      }
      
      if (isSignup) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await updateProfile(user, { displayName: name.trim() });
        await setDoc(doc(db, "teaching_workspaces", user.uid), {
          ownerId: user.uid,
          displayName: `${name.trim()}'s Workspace`,
          email: user.email,
          createdAt: serverTimestamp(),
          subscription: {
            status: 'trial',
            plan: 'individual_trial'
          }
        });
        toast({ title: "Account Created", description: "Welcome to Sonoma Labs!" });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast({ title: "Welcome back", description: "Logged in successfully." });
      }
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!executeRecaptcha) throw new Error('reCAPTCHA not ready.');
      await executeRecaptcha('social_login');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const docRef = doc(db, "teaching_workspaces", user.uid);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        await setDoc(docRef, {
          ownerId: user.uid,
          displayName: `${user.displayName || 'My'}'s Workspace`,
          email: user.email,
          createdAt: serverTimestamp(),
          subscription: { status: 'trial', plan: 'individual_trial' }
        });
      }
      navigate('/dashboard');
    } catch (error: any) {
      if (error.message?.includes('Cross-Origin-Opener-Policy') || error.message?.includes('window.close')) {
         const user = auth.currentUser;
         if (user) {
           const docRef = doc(db, "teaching_workspaces", user.uid);
           const docSnap = await getDoc(docRef);
           if (!docSnap.exists()) {
             await setDoc(docRef, {
               ownerId: user.uid,
               displayName: `${user.displayName || 'My'}'s Workspace`,
               email: user.email,
               createdAt: serverTimestamp(),
               subscription: { status: 'trial', plan: 'individual_trial' }
             });
           }
           navigate('/dashboard');
           return; 
         }
      }
      setError(error.message || 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setResetMessage('Check your inbox.');
      setTimeout(() => setShowForgotPassword(false), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to send email.');
    }
  };

  return (
    <div className="h-screen bg-white text-black font-sans flex selection:bg-black selection:text-white overflow-hidden">
      {/* Visual Side (Hidden on Mobile) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0a0a0a] relative overflow-hidden border-r border-gray-900/30 italic h-full">
        <motion.img 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5 }}
          src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=2070&auto=format&fit=crop" 
          className="absolute inset-0 w-full h-full object-cover grayscale opacity-40"
          alt="Workspace Background"
        />
        {/* Neutral Dark Overlay for Visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
        
        <div className="relative z-10 w-full p-10 lg:p-16 flex flex-col justify-between h-full text-white">
          <div>
            <div className="mb-8">
              <span className="text-2xl tracking-tighter uppercase leading-none flex items-baseline gap-1">
                <span className="font-serif italic lowercase tracking-tight text-3xl text-white">sonoma</span>
                <span className="font-bold tracking-[0.2em] text-[10px] text-blue-400">Workspace</span>
              </span>
            </div>
            <motion.h2 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-5xl lg:text-6xl font-medium tracking-tighter leading-none mb-4"
            >
              The Science <br/>of <span className="text-blue-500 italic drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">Focus.</span>
            </motion.h2>
            <p className="text-lg text-blue-100/80 max-w-xs font-light leading-relaxed">
              Experience the convergence of technology and pedagogical excellence in your personal workspace.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-8 text-[11px] font-bold uppercase tracking-widest text-blue-300">
            <span className="flex items-center gap-2"><Sparkles size={14} className="text-blue-500" /> AI DRIVEN</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-blue-500" /> SECURE GATE</span>
            <span className="flex items-center gap-2"><Heart size={14} className="text-blue-500" /> TEACHER FIRST</span>
          </div>
        </div>
      </div>

      {/* Form Side */}
      <div className="w-full lg:w-1/2 flex flex-col bg-white h-full overflow-y-auto">
        {/* Mobile Branding (Only visible on small screens) */}
        <div className="lg:hidden relative w-full px-6 py-6 bg-[#0a0a0a] overflow-hidden shrink-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/60 to-white z-10" />
          <img 
            src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=800&auto=format&fit=crop" 
            className="absolute inset-0 w-full h-full object-cover grayscale opacity-20"
            alt="Workspace"
          />
          <div className="relative z-20 flex flex-col items-center justify-center">
            <span className="text-2xl tracking-tighter uppercase leading-none flex items-baseline gap-1">
              <span className="font-serif italic lowercase tracking-tight text-3xl text-white">sonoma</span>
              <span className="font-bold tracking-[0.2em] text-[9px] text-blue-400">Workspace</span>
            </span>
            <p className="text-[8px] text-blue-200/70 uppercase tracking-[0.25em] mt-2 font-bold text-center">The Science of Focus</p>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto px-6 py-6 lg:py-8 flex-1 flex flex-col">

          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:text-black transition-all group mb-4 self-start">
            <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <span className="text-[10px] font-bold tracking-[0.2em] text-gray-500 mb-3 block uppercase leading-none italic">02 // AUTHENTICATION</span>
            <h1 className="text-3xl md:text-4xl font-medium tracking-tighter leading-none mb-2">
              {isSignup ? "New Horizon." : "Welcome Back."}
            </h1>
            <p className="text-gray-600 text-xs font-light italic">
              {isSignup ? "Build your professional identity." : "Enter your curated environment."}
            </p>
          </motion.div>

          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-3">
              {isSignup && (
                <div className="group border-b border-black/20 focus-within:border-black transition-all py-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-600 group-focus-within:text-black">Full Name</label>
                  <input 
                    type="text" 
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-transparent border-none focus:ring-0 text-base font-medium py-1.5 px-0 placeholder:text-gray-300"
                    required
                  />
                </div>
              )}
              
              <div className="group border-b border-black/20 focus-within:border-black transition-all py-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-600 group-focus-within:text-black">Email Address</label>
                <input 
                  type="email" 
                  placeholder="name@institution.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-none focus:ring-0 text-base font-medium py-1.5 px-0 placeholder:text-gray-300"
                  required
                />
              </div>

              <div className="group border-b border-black/20 focus-within:border-black transition-all py-1">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-600 group-focus-within:text-black">Password</label>
                  {!isSignup && (
                    <button 
                      type="button" 
                      onClick={() => setShowForgotPassword(true)}
                      className="text-[10px] font-bold uppercase text-gray-600 hover:text-black transition-colors"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <input 
                  type="password" 
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-none focus:ring-0 text-base font-medium py-1.5 px-0 placeholder:text-gray-300"
                  required
                />
              </div>

              {isSignup && (
                <div className="group border-b border-black/20 focus-within:border-black transition-all py-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-600 group-focus-within:text-black">Confirm Password</label>
                  <input 
                    type="password" 
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-transparent border-none focus:ring-0 text-base font-medium py-1.5 px-0 placeholder:text-gray-300"
                    required
                  />
                </div>
              )}
            </div>

            {error && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] font-bold uppercase tracking-widest text-red-600 italic leading-none">
                Error // {error}
              </motion.p>
            )}

            <div className="space-y-3 pt-1">
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-black text-white text-[10px] font-black uppercase tracking-[0.3em] py-4 hover:bg-gray-800 transition-all flex items-center justify-center gap-3 group"
              >
                {loading ? <Loader2 className="animate-spin w-4 h-4" /> : (
                  <>
                    {isSignup ? "Create Account" : "Sign In"}
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <div className="relative flex items-center justify-center">
                <div className="w-full border-t border-gray-100" />
                <span className="absolute bg-white px-3 text-[9px] font-bold uppercase tracking-widest text-gray-400">Social</span>
              </div>

              <button 
                type="button" 
                onClick={handleGoogleSignIn}
                className="w-full border border-gray-100 shadow-sm text-[10px] font-black uppercase tracking-widest py-3 flex items-center justify-center gap-3 hover:border-black hover:shadow-md transition-all bg-white"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                {isSignup ? "Sign up with Google" : "Sign in with Google"}
              </button>
            </div>

            <p className="text-center text-[10px] font-bold uppercase tracking-widest text-gray-500 pt-2">
              {isSignup ? "Have an identity? " : "No workspace? "}
              <button 
                type="button"
                onClick={() => { setIsSignup(!isSignup); setError(null); }}
                className="text-black hover:italic font-black border-b border-black transition-all"
              >
                {isSignup ? "Sign In" : "Register"}
              </button>
            </p>
          </form>

          <footer className="mt-6 pt-4 border-t border-gray-50 flex flex-col gap-2">
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 text-center">
              &copy; 2026 Sonoma Labs // ALL RIGHTS RESERVED
            </p>
          </footer>
        </div>
      </div>

      {/* reCAPTCHA Disclaimer Bottom Right */}
      <div className="fixed bottom-4 right-4 z-50 max-w-[200px] text-right pointer-events-none">
        <p className="text-[8px] font-medium text-gray-400 dark:text-gray-500 leading-tight bg-white/80 dark:bg-black/80 backdrop-blur px-2 py-1 rounded">
          Protected by reCAPTCHA.<br/>
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Privacy</a> &{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline pointer-events-auto hover:text-black">Terms</a> apply.
        </p>
      </div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/90 backdrop-blur-md flex items-center justify-center z-[100] p-8"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="max-w-md w-full"
            >
              <span className="text-[10px] font-bold tracking-[0.4em] text-gray-500 mb-6 block uppercase">03 // RECOVERY</span>
              <h2 className="text-5xl font-medium tracking-tighter mb-12">Lost Access?</h2>
              
              <form onSubmit={handleForgotPassword} className="space-y-8">
                <div className="group border-b border-black/10 focus-within:border-black transition-all py-2">
                  <label className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 group-focus-within:text-black">Recovery Email</label>
                  <input 
                    type="email" 
                    placeholder="name@institution.com"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full bg-transparent border-none focus:ring-0 text-lg font-medium p-0"
                    required
                  />
                </div>
                
                {resetMessage && <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 italic">Success // {resetMessage}</p>}
                
                <div className="flex flex-col gap-4">
                  <button type="submit" className="w-full bg-black text-white text-[12px] font-black uppercase tracking-[0.4em] py-5">Send Reset Link</button>
                  <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-[10px] font-black uppercase tracking-[0.4em] py-5 text-gray-400 hover:text-black transition-colors">Abort Recovery</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IndependentLogin;